import { useEffect, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useLogout } from "react-admin";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useTenantBranding } from "@/hooks/useTenantBranding";
import {
  chooseToViewOldApp,
  readStoredTenantRetirement,
  TENANT_RETIREMENT_EVENT,
} from "@/utils/tenant-retirement";

/**
 * Shown after sign-in to users of a tenant that moved to Jamaat360. No automatic redirect:
 * one button leads to the new app. Platform admins get one extra link to look at the old records.
 */
export default function MovedNotice(): ReactElement | null {
  const navigate = useNavigate();
  const logout = useLogout();
  const { tenantName, logoUrl } = useTenantBranding();
  const [retirement, setRetirement] = useState(readStoredTenantRetirement);

  useEffect(() => {
    const refresh = () => setRetirement(readStoredTenantRetirement());
    window.addEventListener(TENANT_RETIREMENT_EVENT, refresh);
    return () => window.removeEventListener(TENANT_RETIREMENT_EVENT, refresh);
  }, []);

  useEffect(() => {
    // Opened directly while the tenant is not retired: nothing to say here.
    if (!retirement) navigate("/", { replace: true });
  }, [retirement, navigate]);

  if (!retirement) return null;

  const name = tenantName || "This jamaat";
  const address = retirement.newAppUrl.replace(/^https:\/\//, "").replace(/\/$/, "");

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Paper elevation={2} sx={{ width: "100%", maxWidth: 460, p: { xs: 2.5, sm: 3.5 } }}>
        <Stack spacing={2} alignItems="flex-start">
          <Avatar src={logoUrl || "/logo.png"} alt={name} sx={{ width: 56, height: 56 }} />
          <Typography variant="h5" component="h1" sx={{ textWrap: "balance" }}>
            {name} has moved to Jamaat360
          </Typography>
          <Typography>
            Jamaat360 has replaced this app. Your records, receipts and sign-in have been carried
            over.
          </Typography>
          <Typography>
            This old app is now read-only, so nothing you enter here will be saved.
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            component="a"
            href={retirement.newAppUrl}
            rel="noopener"
          >
            Go to the new app
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {address}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in there with the same phone number or email you used here.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Receipts and forms that were sent on WhatsApp still open as before.
          </Typography>
          {retirement.canViewOldData ? (
            <Box
              sx={{
                width: "100%",
                p: 1.5,
                borderRadius: 1,
                bgcolor: (theme) => theme.palette.action.hover,
              }}
            >
              <Typography variant="overline" component="div">
                Platform admins only
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                You can still look at the old records to compare figures. Saving is switched off for
                everyone.
              </Typography>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  chooseToViewOldApp();
                  navigate("/", { replace: true });
                }}
              >
                View the old app
              </Link>
            </Box>
          ) : null}
          <Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <Link component="button" type="button" variant="body2" onClick={() => logout()}>
              Sign out
            </Link>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
